/**
 * drift-lifecycle.ts — display-only lifecycle vocabulary + risk-scale
 * unification for the Hybrid drift queue (WO-1).
 *
 * DSC and TCM each have their own storage-level status vocabulary
 * (`DriftStatus` in types/index.ts vs. the TCM state string in types/tcm.ts)
 * and their own risk-score scale (DSC: 0-1 double; TCM: 0-100 int). Customers
 * see one merged queue, so this module maps both onto a single display
 * vocabulary and a single 0-100 risk scale for RENDERING ONLY — storage
 * states are untouched, and drift-staging.ts's staging predicates key off
 * the raw states by design; nothing here rewires those.
 */

export type DriftDisplayState = 'open' | 'staged' | 'resolved' | 'dismissed'

const DSC_TO_DISPLAY: Record<string, DriftDisplayState> = {
  new: 'open',
  pending: 'open',
  pendingapproval: 'open',
  awaitingremediation: 'staged',
  approved: 'staged',
  remediated: 'resolved',
  rejected: 'dismissed',
  ignored: 'dismissed',
}

const TCM_TO_DISPLAY: Record<string, DriftDisplayState> = {
  active: 'open',
  acceptedpending: 'staged',
  accepted: 'resolved',
  remediated: 'resolved',
  waived: 'dismissed',
  rejected: 'dismissed',
}

/**
 * Maps a source's raw status string to the unified display vocabulary
 * (case-insensitive). An unrecognised or empty status resolves to 'open' —
 * fail-visible: an unmapped state must surface in the queue for a human to
 * act on, never silently disappear into 'resolved' or 'dismissed'.
 */
export function toDisplayState(
  source: 'DSC' | 'TCM',
  status: string | null | undefined,
): DriftDisplayState {
  if (!status) return 'open'
  const table = source === 'DSC' ? DSC_TO_DISPLAY : TCM_TO_DISPLAY
  return table[status.toLowerCase()] ?? 'open'
}

const DISPLAY_STATE_LABEL: Record<DriftDisplayState, string> = {
  open: 'Open',
  staged: 'Staged',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
}

export function displayStateLabel(state: DriftDisplayState): string {
  return DISPLAY_STATE_LABEL[state]
}

/**
 * Rescales a source's risk score onto one 0-100 integer scale. DSC stores a
 * 0-1 double (rescaled x100 and rounded); TCM already stores 0-100 (passed
 * through). Null/undefined/NaN stays null — callers render "-", never a
 * fabricated 0. Clamped to [0, 100] in case either side's stored value is
 * out of its documented range.
 */
export function toRiskScore100(
  source: 'DSC' | 'TCM',
  riskScore: number | null | undefined,
): number | null {
  if (typeof riskScore !== 'number' || Number.isNaN(riskScore)) return null
  const scaled = source === 'DSC' ? riskScore * 100 : riskScore
  return Math.max(0, Math.min(100, Math.round(scaled)))
}
